import { useAppNavigate } from "@/hooks/pane-routing";
import { useEntityNodeObjectActionHandlers } from "@/object-actions/handlers/entityNode";
import { openEntityInDetailPane } from "@/pages/canvas/utils/openNodeInDetailPane";
import {
  openCanvasEntityTabUrl,
  resolveCanvasEntityOpenTabUrl,
} from "@/pages/canvas/utils/resolveCanvasEntityOpenTabUrl";
import { useOpenFileRecord } from "@/panes/file-record-detail";
import { type Entity, getEntityData } from "@cohesive/ui-components";
import { useCallback, useMemo } from "react";

export function useCanvasEntityNavigationHandlers(
  entity: Entity | null | undefined,
  canEdit: boolean,
  onDelete: () => void,
  onOpenOverride?: () => void,
) {
  const appNavigate = useAppNavigate();
  const openFileRecord = useOpenFileRecord();

  const openTabUrl = useMemo(
    () => (entity ? resolveCanvasEntityOpenTabUrl(entity) : null),
    [entity],
  );

  const defaultOnOpen = useCallback(() => {
    if (!entity) return;

    const entityData = getEntityData(entity);
    if (!entityData?.id) return;

    if ("base" in entity && entity.base === "file_record") {
      openFileRecord(String(entityData.id));
      return;
    }

    openEntityInDetailPane(entity, appNavigate);
  }, [appNavigate, entity, openFileRecord]);

  const onOpen = onOpenOverride ?? defaultOnOpen;

  const onOpenInNewTab = useCallback(() => {
    if (!openTabUrl) return;
    openCanvasEntityTabUrl(openTabUrl);
  }, [openTabUrl]);

  const handlers = useEntityNodeObjectActionHandlers({
    entity,
    canEdit,
    onDelete,
    onOpen,
    onOpenInNewTab: openTabUrl ? onOpenInNewTab : undefined,
  });

  return {
    handlers,
    hasWebLink: !!openTabUrl,
    hasDetailView: !!entity,
  };
}
